import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUp } from 'lucide-react';
import { AnnotationAvatar } from './AnnotationBody';
import { useAnnotations } from './AnnotationProvider';
import { filterMentionSuggestions } from './annotationSelectors';

/**
 * One box, the way Linear draws it.
 *
 * The old composer put a bordered textarea and a filled square send button
 * side by side, so a one-line comment box read as two controls with a seam
 * down the middle. Here the border belongs to the box and the textarea is
 * transparent inside it; the send button sits in the box's own footer and
 * only gains colour once there is something to send. The box grows with the
 * text and stops at ten lines.
 *
 * Enter submits; Shift+Enter is a newline; @ opens mentions. Context is
 * locked by the caller — the writer never picks it.
 */
export default function AnnotationComposer({
  context,
  conversationId,
  submitLabel = 'Annotate',
  placeholder = 'Leave an annotation...',
  initialBody = '',
  initialMentions = [],
  onSubmit,
  onCancel,
  autoFocus = true,
  /** 'comment' is the standalone box; 'reply' sits inside a card with an avatar. */
  variant = 'comment',
}) {
  const { mentionUsers, addMention, currentUser } = useAnnotations();
  const [body, setBody] = useState(initialBody);
  const [mentions, setMentions] = useState(initialMentions);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [focused, setFocused] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (autoFocus) textareaRef.current?.focus();
  }, [autoFocus]);

  // Grow to fit, cap at ten lines. Reset first so deleting text shrinks it.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [body]);

  const suggestions = useMemo(
    () => filterMentionSuggestions(
      mentionUsers,
      mentionQuery,
      currentUser?.id,
      currentUser?.email,
    ),
    [mentionUsers, mentionQuery, currentUser?.id, currentUser?.email],
  );

  useEffect(() => {
    if (highlight >= suggestions.length) setHighlight(0);
  }, [suggestions, highlight]);

  const syncMentionQuery = (value, caret) => {
    const upto = value.slice(0, caret);
    const match = upto.match(/@([^\s@]*)$/);
    if (match) {
      setMentionOpen(true);
      setMentionQuery(match[1]);
      setHighlight(0);
    } else {
      setMentionOpen(false);
      setMentionQuery('');
    }
  };

  const insertMention = (user) => {
    if (!user) return;
    const selfId = currentUser?.id != null ? Number(currentUser.id) : NaN;
    const selfEmail = String(currentUser?.email || '').trim().toLowerCase();
    const userEmail = String(user.email || '').trim().toLowerCase();
    if (Number.isFinite(selfId) && Number(user.id) === selfId) return;
    if (selfEmail && userEmail && selfEmail === userEmail) return;
    const el = textareaRef.current;
    const caret = el?.selectionStart ?? body.length;
    const upto = body.slice(0, caret);
    const match = upto.match(/@([^\s@]*)$/);
    const start = match ? caret - match[0].length : caret;
    const nextBody = `${body.slice(0, start)}@${user.name} ${body.slice(caret)}`;
    setBody(nextBody);
    setMentions((prev) => addMention(prev, user));
    setMentionOpen(false);
    setMentionQuery('');
    requestAnimationFrame(() => {
      const pos = start + user.name.length + 2;
      el?.focus();
      el?.setSelectionRange(pos, pos);
    });
  };

  const submit = () => {
    const trimmed = body.trim();
    if (!trimmed) return;
    const kept = mentions.filter((m) => trimmed.includes(`@${m.displayName}`));
    onSubmit?.({
      conversationId,
      context,
      body: trimmed,
      mentions: kept,
    });
    setBody('');
    setMentions([]);
  };

  const onKeyDown = (e) => {
    if (mentionOpen && suggestions.length) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlight((i) => (i + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlight((i) => (i - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(suggestions[highlight] || suggestions[0]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setMentionOpen(false);
        return;
      }
    }
    if (e.key === 'Escape' && onCancel) {
      e.preventDefault();
      e.stopPropagation();
      onCancel();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const ready = Boolean(body.trim());
  // The footer only takes up room once there is something to do with it: an
  // untouched reply box stays a single quiet line, as in the screenshots.
  const showFooter = variant === 'comment' || focused || ready || Boolean(onCancel);

  return (
    <div className="relative">
      <div
        className={`flex items-start gap-2 rounded-lg border bg-surface px-2.5 py-2 transition-colors ${
          focused
            ? 'border-[color:var(--trodo-primary)]'
            : 'border-[color:var(--border-default)]'
        }`}
      >
        {variant === 'reply' ? (
          <div className="mt-0.5 flex-none">
            <AnnotationAvatar name={currentUser?.name} size={20} />
          </div>
        ) : null}

        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <textarea
            ref={textareaRef}
            value={body}
            rows={1}
            placeholder={placeholder}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onChange={(e) => {
              setBody(e.target.value);
              syncMentionQuery(e.target.value, e.target.selectionStart);
            }}
            onKeyDown={onKeyDown}
            className="w-full resize-none border-0 bg-transparent p-0 text-[13px] leading-5 text-[color:var(--text-primary)] outline-none placeholder:text-[color:var(--text-muted)] focus:outline-none focus:ring-0"
          />

          {showFooter ? (
            <div className="flex items-center justify-end gap-1">
              {onCancel ? (
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={onCancel}
                  className="rounded-md px-2 py-1 text-[12px] text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)]"
                >
                  Cancel
                </button>
              ) : null}
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={submit}
                disabled={!ready}
                aria-label={submitLabel}
                title={submitLabel}
                className={`flex h-6 w-6 flex-none items-center justify-center rounded-full transition-colors ${
                  ready
                    ? 'bg-[color:var(--trodo-primary)] text-ink-onBrand'
                    : 'bg-surface-hover text-[color:var(--text-muted)]'
                }`}
              >
                <ArrowUp size={13} strokeWidth={2.5} />
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {mentionOpen && suggestions.length ? (
        // Opens upward. The composer sits at the bottom of a panel, so a
        // list that dropped below it was clipped or scrolled out of view.
        <div className="absolute left-0 right-0 bottom-full z-20 mb-1 overflow-hidden rounded-lg border border-[color:var(--border-default)] bg-surface py-1 shadow-token-lg">
          {/* The address, not just the name.
              Two people on a team can share a display name: and one person
              with two accounts certainly does. This list showed both as an
              identical "Akshit Varsani", so picking the wrong one was a coin
              toss, and the wrong one meant the notification went to an inbox
              nobody was reading and a Slack account that was never connected.
              Nothing in the product could have explained that. */}
          {suggestions.map((user, i) => (
            <button
              key={user.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); insertMention(user); }}
              className={`flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[12px] ${
                i === highlight ? 'bg-surface-hover text-[color:var(--text-primary)]' : 'text-[color:var(--text-secondary)] hover:bg-surface-hover'
              }`}
            >
              <AnnotationAvatar name={user.name} src={user.avatarUrl} size={20} />
              <span className="min-w-0 flex-1 truncate">
                {user.name}
                {user.email ? (
                  <span className="ml-1.5 text-[11px] text-[color:var(--text-muted)]">{user.email}</span>
                ) : null}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
